import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseInit';
import {
  DEFAULT_LINK_REGISTRY_CONFIG,
  resolveNewTripLinkRegistryConfig
} from '../config/entityTypeRegistry';

const META_DOC_PATH = ['_meta', 'linkRegistry'];

function configDocRef(userId, tripId) {
  return doc(db, 'users', userId, 'trips', tripId, ...META_DOC_PATH);
}

export const linkRegistryConfigService = {
  /**
   * @param {string} userId
   * @param {string} tripId
   * @returns {Promise<import('../types/tripLink.types').LinkRegistryConfig>}
   */
  async getLinkRegistryConfig(userId, tripId) {
    const ref = configDocRef(userId, tripId);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) {
      return { ...DEFAULT_LINK_REGISTRY_CONFIG };
    }
    return {
      ...DEFAULT_LINK_REGISTRY_CONFIG,
      ...snapshot.data()
    };
  },

  /**
   * @param {string} userId
   * @param {string} tripId
   * @param {Partial<import('../types/tripLink.types').LinkRegistryConfig>} partialConfig
   */
  async setLinkRegistryConfig(userId, tripId, partialConfig) {
    const current = await this.getLinkRegistryConfig(userId, tripId);
    const next = {
      ...current,
      ...partialConfig,
      updatedAt: new Date().toISOString()
    };
    await setDoc(configDocRef(userId, tripId), next, { merge: true });
    return next;
  },

  /**
   * Writes Phase 3 link registry defaults for a newly created or imported trip.
   * @param {string} userId
   * @param {string} tripId
   * @param {{ extractLegacyLinks?: boolean }} [options]
   * @returns {Promise<{ config: import('../types/tripLink.types').LinkRegistryConfig, extractResult: object|null }>}
   */
  async bootstrapNewTripRegistry(userId, tripId, { extractLegacyLinks = false } = {}) {
    const config = await this.setLinkRegistryConfig(
      userId,
      tripId,
      resolveNewTripLinkRegistryConfig()
    );

    if (!extractLegacyLinks) {
      return { config, extractResult: null };
    }

    const { linkExtractorService } = await import('./linkExtractorService');
    const extractResult = await linkExtractorService.extractLinksFromLegacy(userId, tripId, {
      dryRun: false
    });
    return { config, extractResult };
  },

  /**
   * Post-import hook when trip doc already exists (e.g. after wrapped `addTrip` wrote config).
   * Restores exported meta, or runs legacy extraction when no imported links[].
   */
  async finalizeImportLinkRegistry(userId, tripId, { linkRegistryMeta, hasImportedLinks = false } = {}) {
    if (linkRegistryMeta) {
      const config = await this.setLinkRegistryConfig(userId, tripId, linkRegistryMeta);
      return { config, extractResult: null };
    }

    if (hasImportedLinks) {
      const config = await this.getLinkRegistryConfig(userId, tripId);
      return { config, extractResult: null };
    }

    const { linkExtractorService } = await import('./linkExtractorService');
    const extractResult = await linkExtractorService.extractLinksFromLegacy(userId, tripId, {
      dryRun: false
    });
    const config = await this.getLinkRegistryConfig(userId, tripId);
    return { config, extractResult };
  }
};
